import React, { useEffect, useMemo } from 'react';
import { BlinkingCircle } from '@/components/icons/BlinkingCircle';
import { ClientNameAndAvatar } from '@/components/table/ClientNameAndAvatar';
import { getRenewalStatuses } from '@/hooks/getRenewalStatuses';
import { DEAL_STATUS_TYPES } from '@/utils/constants';
import { Skeleton, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getPolicyData } from '../helper';
import { usePipelineContext } from '@/context/pipelineContext';

const RenderRenewalStatusCell = ({ row: deal }) => {
	const theme = useTheme();
	const {
		active: activeStages,
		inactive: inactiveStages,
		endorsements,
		...pipelineQuery
	} = usePipelineContext();

	const active = activeStages.map((s) =>
		typeof s === 'number' || typeof s === 'string' ? s : s.id,
	);
	const inactive = inactiveStages.map((s) =>
		typeof s === 'number' || typeof s === 'string' ? s : s.id,
	);

	const { showInRed, stage } = getRenewalStatuses({
		deal,
		transactions: [...(deal.transaction ? [deal.transaction] : [])],
	});

	const isEndorsement =
		deal.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS ||
		endorsements.includes(deal?.dealStage);

	// for New Business:
	// 	- if a policy is not linked, then the status should be in progress.
	// 	- if a policy is linked, then the status should be completed
	// 	- if a policy is unlinked from a deal that is in stage ‘invoiced’,
	//    can the stage change back to ‘implementation' (AND complete new business button should show again)
	// 	- If the deal is endorsement in progress, the status should be in progress

	// for Renewal:
	// 	- If the deal stage isn’t invoiced, then it should be ‘In Progress’
	// 	- If the deal stage is invoiced then the status can be ‘Completed’
	// 	- If the deal stage is endorsement in progress, the status should be ‘in progress’

	const isLostDeal = useMemo(() => Boolean(stage?.closed), [stage?.closed]);

	const isActive = active.includes(deal?.dealStage);
	const isInactive = inactive.includes(deal?.dealStage);

	const dealStatus = useMemo(() => {
		if (deal?.status === DEAL_STATUS_TYPES.archived) return 'Archived';
		if (isLostDeal) return 'Lost';
		if (isInactive) return 'Completed';
	
		if (isEndorsement) {
			if (deal.pipeline !== process.env.REACT_APP_PIPELINE_ENDORSEMENTS || isActive) {
				return 'In Progress';
			}
	
			return 'Completed';
		}
	
		if (deal?.policiesLoading) return 'Completed';
	
		const hasNoPolicies = !deal?.policy && deal?.policies?.length === 0;
		const hasNoTransaction = !deal?.transaction && deal?.policies?.length === 0;
	
		return hasNoPolicies || hasNoTransaction || isActive
			? 'In Progress'
			: 'Completed';
	}, [
		deal?.status,
		deal?.pipeline,
		deal?.policiesLoading,
		deal?.policy,
		deal?.transaction,
		deal?.policies?.length,
		isLostDeal,
		isInactive,
		isEndorsement,
		isActive,
	]);

	const isLoading = pipelineQuery.isLoading || Boolean(deal?.policiesLoading);

	const colour = useMemo(() => {
		if (isLoading) return 'rgba(0,0,0,0.5)';
		switch (dealStatus) {
			case 'In Progress':
				return theme.palette.primary.main;
			case 'Lost':
			case 'Archived':
				return 'rgba(0,0,0,0.5)';
			default:
				return '#3FC73F';
		}
	}, [dealStatus, isLoading, theme]);

	const dealStageLabel = useMemo(() => {
		if (deal?.status === DEAL_STATUS_TYPES.archived) {
			return undefined;
		}
		if (isLostDeal) {
			return 'Lost';
		}
		if (isEndorsement) {
			return deal?.pipeline === process.env.REACT_APP_PIPELINE_ENDORSEMENTS ||
				inactive.includes(deal?.dealStage)
				? stage?.label
				: 'Endorsement in progress';
		}
		return stage?.label ?? 'Unknown';
	}, [
		deal?.status,
		deal?.dealStage,
		deal?.pipeline,
		inactive,
		isEndorsement,
		isLostDeal,
		stage?.label,
	]);

	return (
		<Stack direction='row' spacing={1} alignItems={'center'} sx={{ width: '100%' }}>
			<BlinkingCircle fontSize='6px' color={colour} />
			<ClientNameAndAvatar
				title={
					<span style={{ alignContent: 'center' }}>
						{isLoading ? <Skeleton variant='text' width='4em' /> : dealStatus}
					</span>
				}
				subtitle={
					isLoading ? (
						<Skeleton variant='text' width='3em' />
					) : (
						dealStageLabel
					)
				}
			/>
		</Stack>
	);
};
export default RenderRenewalStatusCell;
